import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Stack,
  Typography,
  Box,
  Button,
  TableRow,
  TableCell,
} from "@mui/material";
import {
  NoteAdd as NoteAddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Patient as PatientModal } from "../../../models/Patient";
import { Paths as PathsConstant } from "../../../constants/Route";
import { RowsPerPage as RowsPerPageConstant } from "../../../constants/Table";
import { FormatDisplayHn as FormatDisplayHnUtil } from "../../../utils/InputValidation";
import { DateFormatter as DateFormatterUtil } from "../../../utils/FormatDate";
import { useStores } from "../../../stores";
import {
  GetSearchOperativeNote as GetSearchOperativeNoteService,
  GetCountOperativeNoteBySearch as GetCountOperativeNoteBySearchService,
  DeleteOperativeNoteById as DeleteOperativeNoteByIdService,
} from "../../../services/colorectals/OperativeNote";
import AuthenticatedLayout from "../../../layouts/Authenticated";
import SearchAndFilterByDateAndKeyword from "../../../components/filters/SearchAndFilterByDateAndKeyword";
import SimpleTable from "../../../components/tables/Simple";
import ModalDelete from "../../../components/modals/Delete";
import ModalFail from "../../../components/modals/Fail";
import LoadingWholeScreen from "../../../components/loading/WholeScreen";
import ModalPatientInformation from "../../../components/patients/ModalInformation";
import Sorting from "../../../components/filters/Sorting";

interface Props {}

const List = (props: Props) => {
  const navigate = useNavigate();
  const {
    HistoryStore,
    PatientStore,
    PreoperativeStore,
    PostoperativeStore,
    OperativeNoteStore,
  } = useStores();
  const columns = [
    {
      name: "Records",
      align: "center",
      className: "min-w-160",
    },
    {
      name: "Patient (HN)",
      align: "center",
      className: "min-w-250 txt-link",
    },
    {
      name: "Date",
      align: "center",
      className: "min-w-100",
    },
    {
      name: "Action",
      align: "center",
      className: "",
    },
  ];
  const [searchQuery, setSearchQuery] = useState({
    keyword: "",
    startDate: "",
    endDate: "",
  });
  const [operativeNoteForms, setOperativeNoteForms] = useState([]);
  const [totalOperativeNoteForms, setTotalOperativeNoteForms] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowPerPage, setRowPerPage] = useState(RowsPerPageConstant);
  const [sorting, setSorting] = useState<any>({ created_at: -1, reference_no: -1 });
  const [operativeNoteFormSelected, setOperativeNoteFormSelected] =
    useState<any>(null);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [isShowFailModal, setIsShowFailModal] = useState(false);
  const [isLoadingWholeScreen, setIsLoadingWholeScreen] = useState(false);
  const [isModalPatientInformationOpen, setIsModalPatientInformationOpen] =
    useState(false);
  let onGetSearchOperativeNoteForms = () => {};
  let onGetCountOperativeNoteForms = () => {};

  useEffect(() => {
    PatientStore.onClearStore();
    HistoryStore.onClearStore();
    PreoperativeStore.onClearStore();
    PostoperativeStore.onClearStore();
    OperativeNoteStore.onClearStore();
  }, [
    PatientStore,
    HistoryStore,
    PreoperativeStore,
    PostoperativeStore,
    OperativeNoteStore,
  ]);
  useEffect(() => {
    onGetSearchOperativeNoteForms();
  }, [currentPage, rowPerPage, searchQuery, sorting]);
  useEffect(() => {
    onGetCountOperativeNoteForms();
  }, [searchQuery]);

  onGetSearchOperativeNoteForms = async () => {
    setIsLoading(true);
    setOperativeNoteForms([]);
    const params = {
      ...(searchQuery.keyword && { keyword: searchQuery.keyword }),
      ...(searchQuery.startDate && { startDate: searchQuery.startDate }),
      ...(searchQuery.endDate && { endDate: searchQuery.endDate }),
      ...(sorting && { sort: JSON.stringify({ ...sorting }) }),
      skip: currentPage * rowPerPage,
      limit: rowPerPage,
    };
    const response = await GetSearchOperativeNoteService({ ...params });
    response.success && setOperativeNoteForms(response.data);
    setIsLoading(false);
  };
  onGetCountOperativeNoteForms = async () => {
    setIsLoadingCount(true);
    setTotalOperativeNoteForms(0);
    const params = {
      ...(searchQuery.keyword && { keyword: searchQuery.keyword }),
      ...(searchQuery.startDate && { startDate: searchQuery.startDate }),
      ...(searchQuery.endDate && { endDate: searchQuery.endDate }),
    };
    const response = await GetCountOperativeNoteBySearchService({ ...params });
    response.success && setTotalOperativeNoteForms(response.data);
    setIsLoadingCount(false);
  };
  const onResetSearchPostoperative = () => {
    setSearchQuery({
      keyword: "",
      startDate: "",
      endDate: "",
    });
  };
  const onSubmitSearch = (query: any) => {
    setSearchQuery({ ...query });
    handleChangePage(0, rowPerPage);
  };
  const handleChangePage = (page: number, rowPerPage: number) => {
    setCurrentPage(page);
    setRowPerPage(rowPerPage);
  };
  const handleSorting = (value: any) => {
    setSorting(value);
  };
  const onNavigateToOperativeNotePreview = (operativeNote: any) => {
    navigate(
      `${PathsConstant.colorectalSurgeryOperativeNotePreview}/${operativeNote?.patient?.id}/${operativeNote?.id}`
    );
  };
  const onNavigateToOperativeNoteEdit = (operativeNote: any) => {
    navigate(
      `${PathsConstant.colorectalSurgeryOperativeNoteEdit}/${operativeNote?.patient?.id}/${operativeNote?.id}`
    );
  };
  const handleOpenModalDelete = (operativeNoteForm: any) => {
    setOperativeNoteFormSelected({ ...operativeNoteForm });
    setIsModalDeleteOpen(true);
  };
  const handleCloseModalDelete = () => {
    setOperativeNoteFormSelected(null);
    setIsModalDeleteOpen(false);
  };
  const handleDeleteOperativeNoteForm = async () => {
    setIsModalDeleteOpen(false);
    setIsLoadingWholeScreen(true);
    const response = await DeleteOperativeNoteByIdService(
      operativeNoteFormSelected?.id
    );
    setIsLoadingWholeScreen(false);
    if (response.success) {
      handleCloseModalDelete();
      onGetSearchOperativeNoteForms();
      onGetCountOperativeNoteForms();
    } else {
      setIsShowFailModal(true);
    }
  };
  const handleOpenModalPatientInformation = (patient: PatientModal) => {
    PatientStore.onSetIsReadOnly(true);
    PatientStore.GetPatientById(patient?.id || "");
    setIsModalPatientInformationOpen(true);
  };
  const handleCloseModalPatientInformation = () => {
    setIsModalPatientInformationOpen(false);
    PatientStore.onClearStore();
  };

  return (
    <AuthenticatedLayout>
      <>
        {isLoadingWholeScreen && (
          <LoadingWholeScreen isShowDescription={true} />
        )}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Typography variant="h5">Operative Note</Typography>
          <Box
            marginLeft={{ sm: "auto" }}
            marginTop={{ xs: 2, sm: 0 }}
            width={{ xs: "100%", sm: "auto" }}
          >
            <Button
              variant="contained"
              onClick={() =>
                navigate(
                  `${PathsConstant.colorectalSurgeryOperativeNoteSearchPatient}?formType=operativeNote`
                )
              }
            >
              <NoteAddIcon />
              Create Form
            </Button>
          </Box>
        </Stack>
        <Box marginY={4}>
          <SearchAndFilterByDateAndKeyword
            placeholderKeyword="Search by first name, last name, hn, id card"
            onReset={onResetSearchPostoperative}
            onSearch={(body: any) => onSubmitSearch(body)}
          />
        </Box>
        <Stack marginBottom={2} alignItems="flex-end">
          <Sorting
            defaultSortingValue="Created (Newest)"
            onSorting={(value: any) => handleSorting(value)}
          />
        </Stack>
        <SimpleTable
          columns={columns}
          totalRows={totalOperativeNoteForms}
          page={currentPage}
          isLoading={isLoading || isLoadingCount}
          onChangePage={(page: number, rowPerPage: number) => {
            handleChangePage(page, rowPerPage);
          }}
        >
          <>
            {operativeNoteForms?.map(
              (operativeNoteForm: any, operativeNoteFormIndex: number) => {
                return (
                  <TableRow hover tabIndex={-1} key={operativeNoteFormIndex}>
                    <TableCell align="center" className={columns[0]?.className}>
                      <Typography variant="subtitle1">
                        {(operativeNoteForm.referenceNo &&
                          `Operative / ${operativeNoteForm.referenceNo}`) ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={columns[1]?.className}>
                      <Typography
                        variant="subtitle1"
                        onClick={() => {
                          handleOpenModalPatientInformation(
                            operativeNoteForm.patient
                          );
                        }}
                      >
                        {operativeNoteForm.patient?.firstName}{" "}
                        {operativeNoteForm.patient?.lastName}{" "}
                        {(operativeNoteForm.patient?.hn &&
                          `(${FormatDisplayHnUtil(
                            operativeNoteForm.patient?.hn
                          )})`) ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={columns[2]?.className}>
                      <Typography variant="subtitle1">
                        {(operativeNoteForm.createdAt &&
                          DateFormatterUtil(operativeNoteForm.createdAt)) ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className="cell-action">
                      <Grid
                        container
                        alignItems="center"
                        justifyContent="center"
                        spacing={2}
                      >
                        <Grid item>
                          <Box
                            className="action-button selected-icon"
                            onClick={() =>
                              onNavigateToOperativeNotePreview(
                                operativeNoteForm
                              )
                            }
                          >
                            <SearchIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box
                            className="action-button edit-icon"
                            onClick={() =>
                              onNavigateToOperativeNoteEdit(operativeNoteForm)
                            }
                          >
                            <EditIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box
                            className="action-button delete-icon"
                            onClick={() =>
                              handleOpenModalDelete(operativeNoteForm)
                            }
                          >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </>
        </SimpleTable>
        <ModalDelete
          isOpen={isModalDeleteOpen}
          title="Delete operative note"
          description="Are you sure you want to delete operative note"
          information={`${
            operativeNoteFormSelected?.patient?.firstName || ""
          } ${operativeNoteFormSelected?.patient?.lastName || ""} / ${
            operativeNoteFormSelected?.referenceNo || ""
          }`}
          onClose={handleCloseModalDelete}
          onDelete={handleDeleteOperativeNoteForm}
        />
        <ModalFail
          isOpen={isShowFailModal}
          title="Delete operative note"
          description="Failed to delete a operative note. Please try again."
          onClose={() => {
            setIsShowFailModal(false);
          }}
        />
        <ModalPatientInformation
          isOpen={isModalPatientInformationOpen}
          onClose={handleCloseModalPatientInformation}
        />
      </>
    </AuthenticatedLayout>
  );
};

export default List;
